import React, { FC, ReactNode, useRef, useState } from 'react';
import { Button, Table } from 'antd';
import { InfiniteScroll, WarningCard } from 'common/components';
import { showMessage, showSuccessMessage } from 'common/helpers';
import { LIST_LIMIT_0 } from 'common/config';
import { Bin, Plus } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { IAddress, ICreateAddressPayload } from 'entities/Address/Address.models';
import { AddressCreateCard } from 'entities/Address/components';
import { renderSettingsSubdivisionAddressesRecord } from 'entities/Settings/Settings.helper';
import { ISubdivision } from 'entities/Subdivision/Subdivision.models';
import { SubdivisionAddAddressModal } from 'entities/Subdivision/components';
import { createAddress, getAddressList, selectAddressList, selectIsAddressLoading } from 'entities/Address/Address.slice';
import {
  addSubdivisionAddress,
  deleteSubdivisionAddress,
  selectIsSubdivisionLoading,
  updateSubdivisionAddress,
} from 'entities/Subdivision/Subdivision.slice';
import { selectCurrentUser } from 'entities/User/User.slice';

interface IComponentProps {
  header: ReactNode;
  subdivision: ISubdivision | null;
}

export const SettingsSubdivisionAddressListTab: FC<IComponentProps> = ({ header, subdivision }) => {
  const dispatch = useAppDispatch();
  // address
  const addressList = useAppSelector(selectAddressList);
  const isAddressLoading = useAppSelector(selectIsAddressLoading);
  // subdivision
  const isSubdivisionLoading = useAppSelector(selectIsSubdivisionLoading);
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const [openWarningCard, setOpenWarningCard] = useState<boolean>(false);
  const [openSubdivisionAddAddressModal, setOpenSubdivisionAddAddressModal] = useState<boolean>(false);
  const [openAddressCreateCard, setOpenAddressCreateCard] = useState<boolean>(false);
  const [address, setAddress] = useState<IAddress | null>(null);
  const addAddressInFlightRef = useRef<boolean>(false);

  const subdivisionAddressIds = subdivision?.addresses.map((item) => item.id);
  const filteredAddresses = addressList.filter((item) => !subdivisionAddressIds?.includes(item.id));

  const openAddAddressModal = async () => {
    if (!currentUser) {
      return;
    }

    await dispatch(
      getAddressList({
        accountId: currentUser.account.id,
        limit: LIST_LIMIT_0,
      }),
    );
    setOpenSubdivisionAddAddressModal(true);
  };

  const closeAddAddressModal = () => {
    setOpenSubdivisionAddAddressModal(false);
  };

  const handleAddSubdivisionAddress = async (value: IAddress, onSuccess?: () => void) => {
    if (!subdivision) {
      return;
    }

    // Синхронный guard от двойного сабмита (гонка main в подразделении).
    if (addAddressInFlightRef.current) {
      return;
    }
    addAddressInFlightRef.current = true;

    try {
      await dispatch(
        addSubdivisionAddress({
          subdivisionId: subdivision.id,
          addressId: value.id,
          onSuccess: () => {
            showSuccessMessage('Новый адрес добавлен в подразделение.');
            onSuccess?.();
          },
        }),
      );
    } finally {
      addAddressInFlightRef.current = false;
    }
  };

  const handleUpdateSubdivisionAddress = (value: IAddress) => {
    if (!subdivision) {
      return;
    }

    dispatch(
      updateSubdivisionAddress({
        subdivisionId: subdivision.id,
        addressId: value.id,
        isMain: true,
      }),
    );
  };

  const openDeleteAddressCard = (value: IAddress) => {
    setAddress(value);
    setOpenWarningCard(true);
  };

  const closeDeleteAddressCard = () => {
    setOpenWarningCard(false);
  };

  const handleDeleteSubdivisionAddress = () => {
    if (!subdivision || !address) {
      return;
    }

    dispatch(
      deleteSubdivisionAddress({
        subdivisionId: subdivision.id,
        addressId: address.id,
        onSuccess: () => {
          showMessage({
            // address.city — объект CityShortDto (city-FK), в строку подставляем name.
            content: `Адрес доставки «${[address.postalCode, address.city?.name, address.name].filter(Boolean).join(', ')}» удален.`,
            icon: <Bin className="icon-bin-dark-grey" />,
          });
          setOpenWarningCard(false);
        },
      }),
    );
  };

  const openNewAddressCard = () => {
    setOpenSubdivisionAddAddressModal(false);
    setOpenAddressCreateCard(true);
  };

  const closeNewAddressCard = () => {
    setOpenAddressCreateCard(false);
  };

  const handleCreateAddress = async ({ onSuccess, ...rest }: ICreateAddressPayload) => {
    if (!subdivision) {
      return;
    }

    const addressResponse = await dispatch(createAddress(rest));

    if (!addressResponse) {
      return;
    }

    handleAddSubdivisionAddress(addressResponse, onSuccess);
  };

  return (
    <>
      <InfiniteScroll>
        {header}

        <div className="mb-6">
          <Button className="button-sm primary" icon={<Plus className="icon-plus-white" />} onClick={openAddAddressModal}>
            Добавить адрес
          </Button>
        </div>

        <Table
          className={`${!subdivision?.addresses.length && 'table-empty'} mb-19 subdivision-address-list-settings__table`}
          showHeader={false}
          pagination={false}
          size="middle"
          dataSource={subdivision?.addresses.map((item) => ({ ...item, key: item.id }))}
          columns={renderSettingsSubdivisionAddressesRecord(handleUpdateSubdivisionAddress, openDeleteAddressCard)}
          loading={isAddressLoading}
          locale={{
            emptyText: <span className="text-body color-dark-grey">В подразделении ещё нет адресов.</span>,
          }}
        />
      </InfiniteScroll>

      <SubdivisionAddAddressModal
        open={openSubdivisionAddAddressModal}
        loading={isSubdivisionLoading}
        list={filteredAddresses}
        onCancel={closeAddAddressModal}
        onSubmit={handleAddSubdivisionAddress}
        onCreateClick={openNewAddressCard}
      />

      <AddressCreateCard
        open={openAddressCreateCard}
        loading={isAddressLoading || isSubdivisionLoading}
        onCancel={closeNewAddressCard}
        onSubmit={handleCreateAddress}
      />

      <WarningCard
        open={openWarningCard}
        content="Вы уверены, что хотите удалить адрес доставки?"
        subtitle="Отменить данное действие будет невозможно."
        confirmBtnTitle="Удалить"
        loading={isSubdivisionLoading}
        onConfirm={handleDeleteSubdivisionAddress}
        onClose={closeDeleteAddressCard}
      />
    </>
  );
};
